/*
 * Word of the Day — a curated, hand-written list of interesting words.
 *
 * One entry is shown per calendar day, chosen deterministically from the date
 * (see getWordOfTheDay below), so every visitor sees the same word on the same
 * day and the homepage prerenders to stable, content-rich HTML.
 *
 * To add words: just append entries to WORDS. The cycle length equals the list
 * length, so the more you add, the longer before a word repeats. Keep each
 * `fact` to one crisp, genuinely interesting sentence, and each `example` to a
 * natural, real-world sentence that shows the word in use.
 */

export const WORDS = [

  /* ── Untranslatable loanwords — "there's a word for that" ── */
  {
    word: 'Schadenfreude',
    pos: 'noun',
    pron: 'SHAH-den-froy-duh',
    definition: 'Pleasure or satisfaction felt at someone else’s misfortune.',
    fact: 'Borrowed whole from German, it literally combines Schaden (damage) and Freude (joy).',
    example: 'He tried to hide his schadenfreude when the smug contestant lost in the first round.',
  },
  {
    word: 'Hygge',
    pos: 'noun',
    pron: 'HOO-guh',
    definition: 'A cozy, contented feeling of warmth and togetherness.',
    fact: 'This Danish word has no exact English equivalent, which is exactly why it crossed over unchanged into English.',
    example: 'Candles, wool socks, and a pot of tea — the cabin was pure hygge on a snowy evening.',
  },
  {
    word: 'Saudade',
    pos: 'noun',
    pron: 'sow-DAH-jee',
    definition: 'A deep, wistful longing for someone or something absent, often tinged with love.',
    fact: 'Portuguese speakers consider saudade so untranslatable that November 30 is celebrated as Dia da Saudade.',
    example: 'Looking through old photographs of her hometown filled her with saudade.',
  },
  {
    word: 'Wanderlust',
    pos: 'noun',
    pron: 'WON-der-lust',
    definition: 'A strong, restless desire to travel and explore the world.',
    fact: 'German for "desire to wander," it entered everyday English in the early 20th century and never left.',
    example: 'One backpacking trip through Europe was all it took to spark her wanderlust for life.',
  },
  {
    word: 'Ikigai',
    pos: 'noun',
    pron: 'ee-kee-GAI',
    definition: 'A sense of purpose that makes life feel worth living — the reason you get up in the morning.',
    fact: 'The Japanese concept blends iki (life) and gai (value or worth), and is often mapped where passion, skill, and need overlap.',
    example: 'Teaching kids to read had become her ikigai after decades in the classroom.',
  },
  {
    word: 'Hiraeth',
    pos: 'noun',
    pron: 'HEER-eyeth',
    definition: 'A deep homesickness for a home, place, or time you may not even be able to return to.',
    fact: 'Welsh speakers describe hiraeth as grief for a lost place layered with love and longing all at once.',
    example: 'Decades after emigrating, he still felt a quiet hiraeth for the Welsh valley of his childhood.',
  },
  {
    word: 'Sisu',
    pos: 'noun',
    pron: 'SEE-soo',
    definition: 'Stoic determination and grit in the face of adversity.',
    fact: 'Finns consider sisu a national trait — quiet, unglamorous perseverance rather than loud heroics.',
    example: 'It took real sisu to keep training through that brutal Nordic winter.',
  },
  {
    word: 'Gemütlichkeit',
    pos: 'noun',
    pron: 'guh-MOOT-likh-kite',
    definition: 'A warm feeling of friendliness, coziness, and good cheer, especially in a shared space.',
    fact: 'German beer halls are famous for it — the word covers everything from decor to the mood of the crowd.',
    example: 'The little pub radiated gemütlichkeit, with lantern light and old friends laughing over pints.',
  },
  {
    word: 'Fernweh',
    pos: 'noun',
    pron: 'FAIRN-vay',
    definition: 'A craving to travel to far-off places — literally "farsickness," the opposite of homesickness.',
    fact: 'Where homesickness aches for home, fernweh aches for anywhere else.',
    example: 'Scrolling through travel photos at 2 a.m., she felt a familiar pang of fernweh.',
  },
  {
    word: 'Tsundoku',
    pos: 'noun',
    pron: 'tsoon-DOH-koo',
    definition: 'The habit of acquiring books and letting them pile up unread.',
    fact: 'The Japanese term blends "piling up" with "reading" — a wry nod to good intentions gathering dust.',
    example: 'His nightstand, three books deep in tsundoku, told the true story of his reading ambitions.',
  },
  {
    word: 'Meraki',
    pos: 'noun',
    pron: 'meh-rah-KEE',
    definition: 'The soul, creativity, or love poured into something you make.',
    fact: 'Greek cooks say a dish made "with meraki" carries a bit of the cook’s spirit in every bite.',
    example: 'You could taste the meraki in every dish at her tiny family-run taverna.',
  },
  {
    word: 'Ubuntu',
    pos: 'noun',
    pron: 'oo-BOON-too',
    definition: 'A philosophy of shared humanity — "I am because we are."',
    fact: 'Rooted in Nguni Bantu languages of Southern Africa, ubuntu was central to Desmond Tutu’s writings on reconciliation.',
    example: 'The village’s spirit of ubuntu meant no family faced hardship alone.',
  },
  {
    word: 'Duende',
    pos: 'noun',
    pron: 'dwen-DEH',
    definition: 'A heightened state of emotion, passion, or inspiration, especially in art or performance.',
    fact: 'Spanish poet Federico García Lorca wrote a famous essay arguing true flamenco can’t exist without duende.',
    example: 'The guitarist closed his eyes mid-solo, playing with a duende the whole room could feel.',
  },
  {
    word: 'Jugaad',
    pos: 'noun',
    pron: 'joo-GAAD',
    definition: 'A clever, resourceful fix improvised from whatever is on hand.',
    fact: 'Widely used across South Asia, jugaad has become shorthand in business circles for frugal, scrappy innovation.',
    example: 'With no spare part in sight, the mechanic rigged a jugaad repair using an old bicycle chain.',
  },
  {
    word: 'Komorebi',
    pos: 'noun',
    pron: 'koh-moh-REH-bee',
    definition: 'Sunlight filtering through the leaves of trees.',
    fact: 'Japanese has a long tradition of precise nature words, and komorebi captures a moment most languages need a whole sentence for.',
    example: 'They walked in silence, watching komorebi dance across the forest path.',
  },
  {
    word: 'Sobremesa',
    pos: 'noun',
    pron: 'soh-breh-MEH-sah',
    definition: 'The relaxed time spent lingering at the table talking after a meal is finished.',
    fact: 'Literally "over the table" in Spanish, it names a ritual many cultures practice but few languages bother to name.',
    example: 'Dinner ended an hour ago, but the sobremesa — and the wine — kept going.',
  },
  {
    word: 'Gökotta',
    pos: 'noun',
    pron: 'yuh-KOT-ah',
    definition: 'Waking up early in the morning specifically to go outside and listen to the first birdsong.',
    fact: 'The Swedish word blends "cuckoo" and an old word for an early rising, tied to a springtime tradition.',
    example: 'On the first warm morning of spring, they tried a proper gökotta before work.',
  },
  {
    word: 'Mångata',
    pos: 'noun',
    pron: 'MAWN-gah-tah',
    definition: 'The road-like, shimmering reflection of moonlight on water.',
    fact: 'Swedish literally builds this one from "moon" and "road" — a path only the moon can walk.',
    example: 'A mångata stretched across the lake, leading straight to the horizon.',
  },
  {
    word: 'Uitwaaien',
    pos: 'noun',
    pron: 'OUT-vy-en',
    definition: 'Taking a brisk walk in the wind, mainly to clear your head and feel refreshed.',
    fact: 'The Dutch literally means "to walk in the wind," treating a windy walk as a small act of self-care.',
    example: 'After a stressful week, she headed straight for the coast for some proper uitwaaien.',
  },
  {
    word: 'Kalsarikänni',
    pos: 'noun',
    pron: 'KAHL-sah-ree-kan-nee',
    definition: 'The act of drinking at home alone in your underwear, with no intention of going out.',
    fact: 'Finland’s tourism board once used this famously specific word in an official ad campaign.',
    example: 'After the long week he’d had, a quiet night of kalsarikänni sounded perfect.',
  },
  {
    word: 'Jayus',
    pos: 'noun',
    pron: 'JAH-yus',
    definition: 'A joke told so badly, or that lands so flat, you end up laughing anyway.',
    fact: 'Indonesian speakers use this to describe the "so-bad-it’s-funny" reaction to a truly terrible pun.',
    example: 'Dad’s pun was a total jayus — groan-worthy, yet somehow the whole table cracked up.',
  },
  {
    word: 'Gigil',
    pos: 'noun',
    pron: 'GHEE-gil',
    definition: 'The overwhelming urge to squeeze or pinch something because it is unbearably cute.',
    fact: 'This Filipino word made English dictionaries only recently, after decades of native speakers insisting nothing else captures it.',
    example: 'She let out a squeal of gigil the moment the puppy waddled into the room.',
  },
  {
    word: 'Culaccino',
    pos: 'noun',
    pron: 'koo-lah-CHEE-no',
    definition: 'The ring-shaped mark or stain left on a table by a cold, wet glass.',
    fact: 'Italian has a dedicated word for this, while English speakers usually just point and say "that mark."',
    example: 'A faint culaccino ringed the coffee table where his iced tea had sat all afternoon.',
  },
  {
    word: 'Iktsuarpok',
    pos: 'noun',
    pron: 'eekt-SWAR-pok',
    definition: 'The restless anticipation of waiting for someone, checking outside again and again to see if they’ve arrived.',
    fact: 'It comes from Inuktitut, a language spoken by Inuit communities across the Canadian Arctic.',
    example: 'She felt a familiar iktsuarpok, peeking out the window every few minutes for her sister’s car.',
  },
  {
    word: 'Waldeinsamkeit',
    pos: 'noun',
    pron: 'vahld-EYEN-zahm-kite',
    definition: 'The peaceful feeling of being alone in the woods, connected to nature.',
    fact: 'German combines Wald (forest) and Einsamkeit (solitude) into a single, walkable feeling.',
    example: 'A slow hike through the pines gave him exactly the waldeinsamkeit he’d been craving.',
  },
  {
    word: 'Toska',
    pos: 'noun',
    pron: 'TOS-kah',
    definition: 'A dull, aching sadness of the soul, often with no specific cause.',
    fact: 'Novelist Vladimir Nabokov called toska a word no single English term truly captures, ranging from vague restlessness to sharp anguish.',
    example: 'A quiet toska settled over him on the long train ride home, for no reason he could name.',
  },
  {
    word: 'Kummerspeck',
    pos: 'noun',
    pron: 'KOOM-er-shpek',
    definition: 'Excess weight gained from emotional overeating.',
    fact: 'It translates literally, and rather bluntly, from German as "grief bacon."',
    example: 'A rough breakup left him with a bit of kummerspeck after too many late-night pizza orders.',
  },
  {
    word: 'Age-otori',
    pos: 'noun',
    pron: 'ah-geh-oh-TOH-ree',
    definition: 'The specific misfortune of looking worse after a haircut than before it.',
    fact: 'Japanese is so precise that even this small, universal frustration gets its own word.',
    example: 'One look in the mirror and she knew — total age-otori before the big interview.',
  },
  {
    word: 'Pana Poʻo',
    pos: 'noun',
    pron: 'PAH-nah POH-oh',
    definition: 'Scratching your head as you try to remember where you put something.',
    fact: 'This charming Hawaiian phrase literally means "head scratch."',
    example: 'He stood in the kitchen doing a full pana poʻo, trying to remember where he’d left his keys.',
  },
  {
    word: 'Litost',
    pos: 'noun',
    pron: 'LEE-tosht',
    definition: 'A sudden pang of torment brought on by glimpsing your own misery or inadequacy.',
    fact: 'Novelist Milan Kundera admitted he searched other languages in vain for an equivalent to this Czech word.',
    example: 'A flash of litost hit her as she watched her old rival succeed effortlessly at the very thing she’d struggled with for years.',
  },
  {
    word: 'Mono no aware',
    pos: 'noun',
    pron: 'MOH-noh noh ah-WAH-reh',
    definition: 'A bittersweet awareness of how fleeting and impermanent beautiful things are.',
    fact: 'The phrase is central to Japanese aesthetics — cherry blossoms are beloved partly because they fall so quickly.',
    example: 'Watching the last of the blossoms drift to the ground, she felt a quiet mono no aware.',
  },
  {
    word: 'Torschlusspanik',
    pos: 'noun',
    pron: 'TOR-shloos-pah-nik',
    definition: 'The anxious feeling that time is running out to act on an opportunity.',
    fact: 'German for "gate-closing panic," it originally described the fear of being shut outside a city’s gates at closing time.',
    example: 'A wave of torschlusspanik hit him as the application deadline crept closer.',
  },

  /* ── Batch 2: niche English coinages — "there’s a word for that," part two ── */
  {
    word: 'Sonder',
    pos: 'noun',
    pron: 'SON-der',
    definition: 'The realization that every stranger you pass is living a life as vivid and complicated as your own.',
    fact: 'Writer John Koenig coined it for his project The Dictionary of Obscure Sorrows, and it spread online faster than almost any invented word in recent memory.',
    example: 'A wave of sonder hit her as she watched a thousand lit windows go by from the train.',
  },
  {
    word: 'Limerence',
    pos: 'noun',
    pron: 'LIM-er-ens',
    definition: 'An intense, often involuntary state of romantic infatuation and obsessive longing.',
    fact: 'Psychologist Dorothy Tennov coined the term in her 1979 book Love and Limerence after interviewing hundreds of people about the feeling of falling for someone.',
    example: 'He recognized the signs of limerence — replaying every text, unable to focus on anything else.',
  },
  {
    word: 'Apricity',
    pos: 'noun',
    pron: 'uh-PRIS-i-tee',
    definition: 'The warmth of the sun on a cold winter day.',
    fact: 'This word is genuinely centuries old, first recorded in the 1600s, though it fell out of use until word-lovers revived it online.',
    example: 'They sat on the frosty bench anyway, chasing a little apricity before heading back inside.',
  },
  {
    word: 'Vellichor',
    pos: 'noun',
    pron: 'VEL-i-kor',
    definition: 'The strange wistfulness of used bookstores, full of other people’s forgotten stories.',
    fact: 'Like sonder, this is a modern invented word from The Dictionary of Obscure Sorrows, not a traditional dictionary entry — but it has caught on among readers everywhere.',
    example: 'The moment she stepped into the dusty secondhand shop, the vellichor was unmistakable.',
  },
  {
    word: 'Eunoia',
    pos: 'noun',
    pron: 'yoo-NOY-uh',
    definition: 'Beautiful thinking; a state of genuine goodwill toward others.',
    fact: 'Eunoia is famous in word-game circles as the shortest English word containing all five vowels, each used exactly once.',
    example: 'The mediator approached every disagreement with a rare eunoia, always assuming good intent.',
  },
  {
    word: 'Collywobbles',
    pos: 'noun',
    pron: 'KOL-ee-wob-ulz',
    definition: 'A nervous, fluttery stomach ache, often brought on by anxiety.',
    fact: 'Despite sounding invented for a children’s book, it is a genuine, centuries-old informal English word.',
    example: 'Standing backstage before her first solo, she had a serious case of the collywobbles.',
  },
  {
    word: 'Widdershins',
    pos: 'adverb',
    pron: 'WID-er-shinz',
    definition: 'Moving in a direction contrary to the sun — counterclockwise.',
    fact: 'In older folklore, walking widdershins around a church or landmark was considered unlucky, even a way to invite bad luck.',
    example: 'The old superstition warned against ever circling the well widdershins after dark.',
  },
  {
    word: 'Snickersnee',
    pos: 'noun',
    pron: 'SNIK-er-snee',
    definition: 'An old word for a large knife, or a scuffle fought with one.',
    fact: 'It survives today mostly thanks to Gilbert and Sullivan’s operetta The Mikado: "As I gnashed my teeth, when from its sheath, I drew my snickersnee."',
    example: 'In the old sea shanty, the pirates settled the argument with a snickersnee.',
  },
  {
    word: 'Cattywampus',
    pos: 'adjective',
    pron: 'kat-ee-WOM-pus',
    definition: 'Crooked, askew, or positioned diagonally instead of straight.',
    fact: 'This American colloquialism is also used more loosely to mean "in complete disarray."',
    example: 'After the move, every picture frame on the wall hung slightly cattywampus.',
  },
  {
    word: 'Lollygag',
    pos: 'verb',
    pron: 'LOL-ee-gag',
    definition: 'To dawdle or waste time instead of getting on with something.',
    fact: 'It has been part of American slang since the 1800s, and no one is entirely sure where it came from.',
    example: 'Stop lollygagging and finish your homework before dinner.',
  },
  {
    word: 'Gobbledygook',
    pos: 'noun',
    pron: 'GOB-ul-dee-gook',
    definition: 'Language, especially official or technical jargon, that is confusing or nearly meaningless.',
    fact: 'A U.S. congressman coined it in the 1940s, saying overblown bureaucratic language reminded him of a turkey’s gobble.',
    example: 'The insurance policy was three pages of pure gobbledygook.',
  },
  {
    word: 'Rigmarole',
    pos: 'noun',
    pron: 'RIG-uh-muh-rohl',
    definition: 'A long, complicated, and often needlessly confusing procedure.',
    fact: 'One theory traces it back to "ragman roll," a medieval English legal document that unrolled into a long, rambling list.',
    example: 'Returning the faulty printer turned into a whole rigmarole of forms and phone calls.',
  },

  /* ── Batch 2: word-nerd — the vocabulary of vocabulary ── */
  {
    word: 'Portmanteau',
    pos: 'noun',
    pron: 'port-MAN-toh',
    definition: 'A word formed by blending the sounds and meanings of two others, like "brunch" or "smog."',
    fact: 'Lewis Carroll popularized the term, comparing the blended word to a portmanteau suitcase that opens into two compartments.',
    example: '"Spork" is a handy portmanteau of "spoon" and "fork."',
  },
  {
    word: 'Spoonerism',
    pos: 'noun',
    pron: 'SPOO-ner-iz-um',
    definition: 'An accidental swap of the initial sounds of two or more words, often to comic effect.',
    fact: 'It’s named for Reverend William Archibald Spooner, an Oxford don reputedly prone to saying things like "a well-boiled icicle" for "a well-oiled bicycle."',
    example: 'He meant to say "lighting a fire," but the spoonerism came out as "fighting a liar."',
  },
  {
    word: 'Malapropism',
    pos: 'noun',
    pron: 'MAL-uh-prop-iz-um',
    definition: 'The mistaken use of a word for a similar-sounding one, usually with an unintentionally funny result.',
    fact: 'It’s named after Mrs. Malaprop, a character in Richard Brinsley Sheridan’s 1775 play The Rivals, whose very name puns on the French "mal à propos" — inappropriate.',
    example: 'Calling the meeting "a real pineapple of success" instead of "pinnacle" was a classic malapropism.',
  },
  {
    word: 'Eggcorn',
    pos: 'noun',
    pron: 'EG-korn',
    definition: 'A word or phrase that results from mishearing another, yet still makes a kind of logical sense.',
    fact: 'Linguists coined the term in 2003 from exactly this kind of slip — someone had written "egg corn" instead of "acorn."',
    example: '"For all intensive purposes" is one of the most common eggcorns of "for all intents and purposes."',
  },
  {
    word: 'Aptronym',
    pos: 'noun',
    pron: 'AP-truh-nim',
    definition: 'A name that seems amusingly well-suited to its owner’s job or personality.',
    fact: 'The term is often credited to newspaper columnist Franklin P. Adams, who cleverly rearranged the letters of "appropriate" to form it.',
    example: 'A dentist named Dr. Payne is a textbook aptronym.',
  },
  {
    word: 'Tautonym',
    pos: 'noun',
    pron: 'TAW-tuh-nim',
    definition: 'A scientific name in which the genus and species names are identical, like Bison bison.',
    fact: 'Gorilla gorilla and Rattus rattus are two more real examples straight out of biology textbooks.',
    example: 'The American bison’s scientific tautonym, Bison bison, is easy for students to remember precisely because it repeats.',
  },
  {
    word: 'Heteronym',
    pos: 'noun',
    pron: 'HET-er-uh-nim',
    definition: 'A word spelled the same as another but pronounced differently, with a different meaning.',
    fact: '"Lead" the metal and "lead" the verb are heteronyms — same spelling, completely different sounds and meanings.',
    example: 'The two heteronyms "bow" (to bend) and "bow" (a ribbon knot) trip up new readers all the time.',
  },
  {
    word: 'Contronym',
    pos: 'noun',
    pron: 'KON-truh-nim',
    definition: 'A word that can mean the opposite of itself depending on context, also called a Janus word.',
    fact: '"Cleave" is the classic example — it can mean to split apart or to bind tightly together.',
    example: '"Sanction" is a tricky contronym: a government can sanction (permit) an action, or sanction (penalize) a country.',
  },
  {
    word: 'Lipogram',
    pos: 'noun',
    pron: 'LIP-oh-gram',
    definition: 'A piece of writing that deliberately avoids using one particular letter.',
    fact: 'The most famous example is Ernest Vincent Wright’s 1939 novel Gadsby — roughly 50,000 words long, without a single letter E.',
    example: 'Writing even a short lipogram avoiding the letter E turns out to be surprisingly difficult.',
  },
  {
    word: 'Acronym',
    pos: 'noun',
    pron: 'AK-ruh-nim',
    definition: 'A word formed from the initial letters of a phrase and pronounced as a single word, like "scuba" or "laser."',
    fact: 'It differs from an initialism, where the letters are pronounced individually, like "FBI" or "URL."',
    example: '"NASA" is an acronym; "USA" is technically an initialism, since we say the letters one by one.',
  },
  {
    word: 'Backronym',
    pos: 'noun',
    pron: 'BAK-ruh-nim',
    definition: 'A phrase invented after the fact to fit an existing word or acronym, as if it had been the original source.',
    fact: '"SOS" is often explained as standing for "Save Our Ship," but it was actually chosen simply because its Morse pattern — three dots, three dashes, three dots — is unmistakable.',
    example: 'Some claim "golf" comes from "Gentlemen Only, Ladies Forbidden," but that’s a well-known backronym with no historical basis.',
  },
  {
    word: 'Neologism',
    pos: 'noun',
    pron: 'nee-OL-uh-jiz-um',
    definition: 'A newly coined word or expression that hasn’t yet become part of mainstream, established language.',
    fact: 'The word itself is built from Greek neo- (new) and logos (word) — a fittingly self-descriptive term.',
    example: '"Doomscrolling" started as internet slang, a neologism now common enough to appear in major dictionaries.',
  },

  /* ── Batch 2: words born from books, plays, and real people ── */
  {
    word: 'Chortle',
    pos: 'verb',
    pron: 'CHOR-tul',
    definition: 'To laugh in a gleeful, snorting sort of way.',
    fact: 'Lewis Carroll invented it for his poem "Jabberwocky," blending "chuckle" and "snort" into one word.',
    example: 'He let out a chortle at the punchline before anyone else in the room got the joke.',
  },
  {
    word: 'Galumph',
    pos: 'verb',
    pron: 'guh-LUMF',
    definition: 'To move in a clumsy, heavy, triumphant sort of way.',
    fact: 'Another Lewis Carroll invention from "Jabberwocky," widely believed to blend "gallop" and "triumphant."',
    example: 'The dog galumphed across the yard, thrilled and utterly uncoordinated.',
  },
  {
    word: 'Robot',
    pos: 'noun',
    pron: 'ROH-bot',
    definition: 'A machine capable of carrying out tasks automatically.',
    fact: 'Playwright Karel Čapek introduced the word in his 1920 play R.U.R., reportedly at the suggestion of his brother Josef — from the Czech robota, meaning forced labor.',
    example: 'A small robot glided across the warehouse floor, sorting packages by size.',
  },
  {
    word: 'Utopia',
    pos: 'noun',
    pron: 'yoo-TOH-pee-uh',
    definition: 'An imagined place or state in which everything is perfect.',
    fact: 'Thomas More coined it in 1516 from Greek for "no place" — a sly pun on eu-topos, "good place."',
    example: 'The commune was founded as a kind of utopia, though reality never quite matched the dream.',
  },
  {
    word: 'Pandemonium',
    pos: 'noun',
    pron: 'pan-duh-MOH-nee-um',
    definition: 'Wild uproar, chaos, or disorder.',
    fact: 'John Milton invented the word in Paradise Lost (1667) as the name of the capital of Hell, from Greek roots meaning "all demons."',
    example: 'Pandemonium broke out in the stadium the instant the winning goal went in.',
  },
  {
    word: 'Yahoo',
    pos: 'noun',
    pron: 'YAH-hoo',
    definition: 'A crude, uncultured, or brutish person.',
    fact: 'Jonathan Swift invented the word for the brutish, humanlike creatures in Gulliver’s Travels (1726), long before it named a search engine.',
    example: 'The old western cast the outlaws as loud, reckless yahoos terrorizing the town.',
  },
  {
    word: 'Gargantuan',
    pos: 'adjective',
    pron: 'gar-GAN-choo-un',
    definition: 'Enormous; gigantic in size.',
    fact: 'It comes from Gargantua, the giant hero of François Rabelais’ 16th-century satirical novels.',
    example: 'A gargantuan pile of leaves awaited them in the backyard after the storm.',
  },
  {
    word: 'Nerd',
    pos: 'noun',
    pron: 'nurd',
    definition: 'A person deeply, enthusiastically knowledgeable about a particular subject, often a studious or technical one.',
    fact: 'One of the earliest print appearances of the word was in Dr. Seuss’s If I Ran the Zoo (1950), naming a small, disgruntled creature.',
    example: 'She wears "word game nerd" as a badge of honor.',
  },
  {
    word: 'Catch-22',
    pos: 'noun',
    pron: 'kach-twen-tee-TOO',
    definition: 'A frustrating, paradoxical situation with no real way out, because the rules that trap you also justify each other.',
    fact: 'It comes straight from the title of Joseph Heller’s 1961 novel, in which a fictional military regulation traps its characters in exactly this way.',
    example: 'It was a real catch-22: he needed experience to get the job, but couldn’t get experience without a job.',
  },
  {
    word: 'Kafkaesque',
    pos: 'adjective',
    pron: 'kahf-kuh-ESK',
    definition: 'Marked by a nightmarish, illogical complexity, especially in bureaucracy.',
    fact: 'It’s named for writer Franz Kafka, whose fiction is full of characters trapped by senseless, oppressive systems.',
    example: 'Getting the paperwork corrected turned into a Kafkaesque ordeal of forms referencing other forms.',
  },
  {
    word: 'Orwellian',
    pos: 'adjective',
    pron: 'or-WEL-ee-un',
    definition: 'Describing a situation of oppressive control, surveillance, or manipulation of truth.',
    fact: 'It’s named for George Orwell, whose novel 1984 gave the world "Big Brother" and "doublethink."',
    example: 'Critics called the new surveillance law downright Orwellian.',
  },
  {
    word: 'Quark',
    pos: 'noun',
    pron: 'kwork',
    definition: 'A fundamental subatomic particle that combines to form protons and neutrons.',
    fact: 'Physicist Murray Gell-Mann named the particle after the line "Three quarks for Muster Mark" in James Joyce’s Finnegans Wake.',
    example: 'The physics lecture explained how three quarks combine to build a single proton.',
  },
  {
    word: 'Boycott',
    pos: 'noun',
    pron: 'BOI-kot',
    definition: 'A refusal to buy, use, or engage with something, usually as a form of organized protest.',
    fact: 'It’s named for Captain Charles Boycott, an English land agent in 19th-century Ireland who was ostracized by his entire community in 1880.',
    example: 'Consumers organized a boycott of the brand after the scandal broke.',
  },
  {
    word: 'Sandwich',
    pos: 'noun',
    pron: 'SAND-wich',
    definition: 'Two or more slices of bread with a filling in between.',
    fact: 'Popular legend credits John Montagu, the 4th Earl of Sandwich, who supposedly asked for meat between bread so he wouldn’t have to leave the gaming table.',
    example: 'She packed a turkey sandwich for the long train ride.',
  },
  {
    word: 'Silhouette',
    pos: 'noun',
    pron: 'sil-oo-ET',
    definition: 'A dark outline or shape seen against a lighter background.',
    fact: 'It’s named for Étienne de Silhouette, a French finance minister in 1759 known for his austere cost-cutting, whose name became shorthand for anything cheap or simplified.',
    example: 'At dusk, the mountains formed a jagged silhouette against the orange sky.',
  },
  {
    word: 'Guillotine',
    pos: 'noun',
    pron: 'GHIL-uh-teen',
    definition: 'A device with a heavy blade used historically for beheading.',
    fact: 'It’s named for Dr. Joseph-Ignace Guillotin, who proposed it as a quicker, more humane execution method — though he didn’t invent the device and was reportedly troubled by its association with his name.',
    example: 'The museum exhibit included a full-size replica of a French Revolution-era guillotine.',
  },
  {
    word: 'Nicotine',
    pos: 'noun',
    pron: 'NIK-uh-teen',
    definition: 'The addictive chemical compound found naturally in tobacco plants.',
    fact: 'It’s named for Jean Nicot, the French diplomat who introduced tobacco to the French court in the 1560s.',
    example: 'The patch delivers a small, steady dose of nicotine to ease cravings.',
  },
  {
    word: 'Maverick',
    pos: 'noun',
    pron: 'MAV-rik',
    definition: 'An independent-minded person who refuses to follow the usual conventions of a group.',
    fact: 'It’s named for Samuel Maverick, a 19th-century Texas rancher famous for not branding his cattle.',
    example: 'The company’s founder was a maverick, ignoring industry norms at every turn.',
  },
  {
    word: 'Mesmerize',
    pos: 'verb',
    pron: 'MEZ-mer-ize',
    definition: 'To completely captivate someone’s attention, as if hypnotizing them.',
    fact: 'It’s named for Franz Anton Mesmer, an 18th-century physician whose theory of "animal magnetism" laid the groundwork for modern hypnotism.',
    example: 'The dancers’ perfectly synchronized movements mesmerized the entire audience.',
  },
  {
    word: 'Chauvinism',
    pos: 'noun',
    pron: 'SHOH-vin-iz-um',
    definition: 'Excessive, biased loyalty to one’s own group, often paired with contempt for others.',
    fact: 'It traces back to Nicolas Chauvin, a soldier said to be almost comically devoted to Napoleon — though historians debate how much of his story is legend.',
    example: 'The old textbook was criticized for its cultural chauvinism, praising one nation while dismissing all others.',
  },
  {
    word: 'Braille',
    pos: 'noun',
    pron: 'brayl',
    definition: 'A tactile writing system of raised dots that can be read by touch.',
    fact: 'It’s named for Louis Braille, a French educator who invented the system in the 1820s after losing his sight as a child.',
    example: 'The elevator buttons included braille labels alongside the printed numbers.',
  },
  {
    word: 'Bowdlerize',
    pos: 'verb',
    pron: 'BOHD-ler-ize',
    definition: 'To remove material considered indecent or offensive from a text, often to the point of distorting it.',
    fact: 'It’s named for Thomas Bowdler, who published a heavily censored "Family Shakespeare" in 1807.',
    example: 'Critics complained the school edition had been bowdlerized almost beyond recognition.',
  },
  {
    word: 'Draconian',
    pos: 'adjective',
    pron: 'druh-KOH-nee-un',
    definition: 'Excessively harsh or severe, especially referring to laws or punishments.',
    fact: 'It’s named for Draco, an ancient Athenian lawmaker whose 7th-century BC legal code punished even minor offenses with death.',
    example: 'Employees pushed back hard against the company’s new draconian attendance policy.',
  },
  {
    word: 'Herculean',
    pos: 'adjective',
    pron: 'hur-kyoo-LEE-un',
    definition: 'Requiring or showing enormous strength or effort.',
    fact: 'It’s named for Hercules, the mythical hero famed for completing twelve seemingly impossible labors.',
    example: 'Cleaning out the flooded basement turned into a herculean task.',
  },

  /* ── Batch 2: the surprising origins of everyday words ── */
  {
    word: 'Ketchup',
    pos: 'noun',
    pron: 'KECH-up',
    definition: 'A sweet-and-tangy tomato-based condiment.',
    fact: 'Most linguists trace it to a Hokkien Chinese fermented fish sauce called kê-tsiap, which traveled through Malay trade routes long before it ever met a tomato.',
    example: 'He squeezed a generous swirl of ketchup onto his fries.',
  },
  {
    word: 'Avocado',
    pos: 'noun',
    pron: 'av-uh-KAH-doh',
    definition: 'A pear-shaped fruit with rich, buttery green flesh.',
    fact: 'It comes from the Nahuatl word āhuacatl, which also happened to mean "testicle," likely due to the fruit’s shape.',
    example: 'She mashed a ripe avocado onto a slice of toast for breakfast.',
  },
  {
    word: 'Marmalade',
    pos: 'noun',
    pron: 'MAR-muh-layd',
    definition: 'A fruit preserve, traditionally made from citrus, that includes the peel.',
    fact: 'It comes from the Portuguese marmelada, originally a thick paste made from quince, not oranges at all.',
    example: 'He spread a thin layer of orange marmalade on his morning toast.',
  },
  {
    word: 'Salary',
    pos: 'noun',
    pron: 'SAL-uh-ree',
    definition: 'A fixed regular payment made for work, usually monthly or annually.',
    fact: 'It’s often explained as coming from the Latin salarium, tied to sal (salt) — though historians debate exactly how salt payments and Roman wages connected.',
    example: 'She negotiated a higher salary before accepting the new position.',
  },
  {
    word: 'Companion',
    pos: 'noun',
    pron: 'kum-PAN-yun',
    definition: 'A person who spends time with or accompanies another.',
    fact: 'It literally builds from Latin com- (with) and panis (bread) — a companion is, at its root, someone you share bread with.',
    example: 'The old dog had been his loyal companion for twelve years.',
  },
  {
    word: 'Muscle',
    pos: 'noun',
    pron: 'MUS-ul',
    definition: 'Fibrous tissue in the body capable of contracting to produce movement.',
    fact: 'It comes from the Latin musculus, "little mouse," since a flexing bicep reminded Romans of a mouse rippling under the skin.',
    example: 'Years of climbing had built noticeable muscle in her forearms.',
  },
  {
    word: 'Disaster',
    pos: 'noun',
    pron: 'diz-AS-ter',
    definition: 'A sudden event causing great damage or suffering.',
    fact: 'It comes from Italian disastro, built from dis- (bad) and astro (star) — literally "bad star," reflecting old beliefs that catastrophes were written in the sky.',
    example: 'The failed product launch turned into a full-blown public relations disaster.',
  },
  {
    word: 'Nightmare',
    pos: 'noun',
    pron: 'NITE-mair',
    definition: 'A frightening or deeply unpleasant dream.',
    fact: 'The "mare" here has nothing to do with horses — in Old English, a mare was a demon believed to sit on a sleeper’s chest.',
    example: 'He woke in a cold sweat after a nightmare about missing his own flight.',
  },
  {
    word: 'Clue',
    pos: 'noun',
    pron: 'kloo',
    definition: 'A piece of evidence or information that helps solve a problem or mystery.',
    fact: 'It’s a variant spelling of "clew," a ball of thread — a nod to the thread Theseus used to navigate the Minotaur’s labyrinth.',
    example: 'The detective found a single clue that finally cracked the case.',
  },
  {
    word: 'Tantalize',
    pos: 'verb',
    pron: 'TAN-tuh-lize',
    definition: 'To torment someone by keeping something desirable just out of reach.',
    fact: 'It’s named for Tantalus, a figure from Greek mythology condemned to stand in water beneath fruit he could never quite grasp.',
    example: 'The smell of fresh bread tantalized everyone waiting in the long bakery line.',
  },
  {
    word: 'Panic',
    pos: 'noun',
    pron: 'PAN-ik',
    definition: 'A sudden, overwhelming feeling of fear or anxiety.',
    fact: 'It’s named for Pan, the Greek god of the wild, believed to strike sudden, groundless terror into travelers in lonely places.',
    example: 'A wave of panic swept through the crowd when the fire alarm sounded.',
  },
  {
    word: 'Cereal',
    pos: 'noun',
    pron: 'SEER-ee-ul',
    definition: 'An edible grain, or a breakfast food made from processed grains.',
    fact: 'It’s named for Ceres, the Roman goddess of agriculture and grain.',
    example: 'He poured a bowl of cereal and ate it standing at the counter.',
  },
  {
    word: 'February',
    pos: 'noun',
    pron: 'FEB-roo-air-ee',
    definition: 'The second month of the year, and the shortest.',
    fact: 'It’s named for Februa, an ancient Roman festival of purification and cleansing held during this time of year.',
    example: 'The shortest month, February, still manages to feel like the longest one of winter.',
  },
  {
    word: 'Volcano',
    pos: 'noun',
    pron: 'vol-KAY-noh',
    definition: 'An opening in the Earth’s crust through which lava, gas, and ash can erupt.',
    fact: 'It’s named for Vulcan, the Roman god of fire and metalworking.',
    example: 'The dormant volcano hadn’t erupted in over four hundred years.',
  },

  /* ── Batch 2: collective nouns for animals ── */
  {
    word: 'Murder',
    pos: 'noun',
    pron: 'MUR-der',
    definition: 'The collective noun for a group of crows.',
    fact: 'It’s one of dozens of quirky "terms of venery" recorded in the 15th-century Book of Saint Albans, a guide to hunting and courtly language.',
    example: 'A murder of crows scattered noisily from the bare oak as the truck approached.',
  },
  {
    word: 'Parliament',
    pos: 'noun',
    pron: 'PAR-luh-ment',
    definition: 'The collective noun for a group of owls.',
    fact: 'The term likely nods to owls’ reputation for wisdom — fittingly gathered like lawmakers in session.',
    example: 'A parliament of owls watched silently from the barn rafters.',
  },
  {
    word: 'Crash',
    pos: 'noun',
    pron: 'krash',
    definition: 'The collective noun for a group of rhinoceroses.',
    fact: 'It’s a rare case where the collective noun perfectly matches the animal’s sheer physical presence.',
    example: 'A crash of rhinoceroses grazed near the watering hole at dawn.',
  },
  {
    word: 'Shrewdness',
    pos: 'noun',
    pron: 'SHROOD-ness',
    definition: 'The collective noun for a group of apes.',
    fact: 'Like "murder" and "parliament," it comes from the same tradition of playful medieval collective nouns.',
    example: 'A shrewdness of apes groomed each other in the shade of the enclosure.',
  },
  {
    word: 'Business',
    pos: 'noun',
    pron: 'BIZ-ness',
    definition: 'The collective noun for a group of ferrets.',
    fact: 'It captures how busy and purposeful a group of ferrets always seems to be.',
    example: 'A whole business of ferrets tumbled out of their tunnel, all at once.',
  },
  {
    word: 'Cackle',
    pos: 'noun',
    pron: 'KAK-ul',
    definition: 'The collective noun for a group of hyenas.',
    fact: 'It plays directly on the hyena’s famous, laughter-like call.',
    example: 'A cackle of hyenas circled the edge of the savanna at dusk.',
  },
  {
    word: 'Bloat',
    pos: 'noun',
    pron: 'bloht',
    definition: 'The collective noun for a group of hippopotamuses.',
    fact: 'It’s a blunt, memorable nod to their massive, rounded bulk.',
    example: 'A bloat of hippos wallowed together in the muddy shallows.',
  },
  {
    word: 'Kaleidoscope',
    pos: 'noun',
    pron: 'kuh-LY-duh-skohp',
    definition: 'The collective noun for a group of butterflies.',
    fact: 'It borrows its name from the swirling, colorful optical toy — a fitting image for butterflies in flight.',
    example: 'A kaleidoscope of monarchs drifted through the garden all afternoon.',
  },
  {
    word: 'Ambush',
    pos: 'noun',
    pron: 'AM-bush',
    definition: 'The collective noun for a group of tigers.',
    fact: 'It reflects the tiger’s hunting style — solitary and stealthy, so an "ambush" is a genuinely rare sight.',
    example: 'Wildlife photographers rarely capture an entire ambush of tigers together in the wild.',
  },
  {
    word: 'Charm',
    pos: 'noun',
    pron: 'charm',
    definition: 'The collective noun for a group of hummingbirds.',
    fact: 'Few collective nouns match their subject as sweetly as this one.',
    example: 'A charm of hummingbirds hovered around the feeder, wings a blur.',
  },
  {
    word: 'Unkindness',
    pos: 'noun',
    pron: 'un-KYND-ness',
    definition: 'The collective noun for a group of ravens.',
    fact: 'It stems from old folklore that painted ravens as ominous or ill-tempered birds.',
    example: 'An unkindness of ravens perched along the fence, watching the farmhouse.',
  },
  {
    word: 'Smack',
    pos: 'noun',
    pron: 'smak',
    definition: 'The collective noun for a group of jellyfish.',
    fact: 'It’s an oddly punchy name for a creature with no bones, brain, or heart.',
    example: 'Swimmers steered well clear of the smack of jellyfish drifting near the pier.',
  },
  {
    word: 'Romp',
    pos: 'noun',
    pron: 'romp',
    definition: 'The collective noun for a group of otters.',
    fact: 'It perfectly captures how playful otters look tumbling together in the water.',
    example: 'A romp of otters slid down the muddy riverbank again and again, purely for fun.',
  },
  {
    word: 'Prickle',
    pos: 'noun',
    pron: 'PRIK-ul',
    definition: 'The collective noun for a group of porcupines.',
    fact: 'It’s about as literal as collective nouns get.',
    example: 'A prickle of porcupines huddled at the base of the old pine tree.',
  },
  {
    word: 'Conspiracy',
    pos: 'noun',
    pron: 'kun-SPEER-uh-see',
    definition: 'The collective noun for a group of lemurs.',
    fact: 'No one is quite sure why lemurs, specifically, earned such a dramatic-sounding collective noun.',
    example: 'A conspiracy of lemurs sunbathed together, arms outstretched, on the forest floor.',
  },

  /* ── Batch 2: science and nature words worth knowing ── */
  {
    word: 'Bioluminescence',
    pos: 'noun',
    pron: 'by-oh-loo-muh-NES-ens',
    definition: 'Light produced naturally by a living organism through a chemical reaction.',
    fact: 'It lights up everything from fireflies and anglerfish to entire waves of glowing plankton at night.',
    example: 'The kayakers gasped as their paddles stirred up bioluminescence in the dark water.',
  },
  {
    word: 'Metamorphosis',
    pos: 'noun',
    pron: 'met-uh-MOR-fuh-sis',
    definition: 'A dramatic transformation from one form to a completely different one.',
    fact: 'Inside its chrysalis, a caterpillar breaks down into a kind of cellular soup before reassembling into a butterfly.',
    example: 'Watching a tadpole’s metamorphosis into a frog is a classic school science project.',
  },
  {
    word: 'Chrysalis',
    pos: 'noun',
    pron: 'KRIS-uh-lis',
    definition: 'The hardened protective casing in which a caterpillar transforms into a butterfly.',
    fact: 'Technically, "chrysalis" refers specifically to butterflies — moths spin a "cocoon" instead.',
    example: 'They found an empty chrysalis hanging from the milkweed stem.',
  },
  {
    word: 'Symbiosis',
    pos: 'noun',
    pron: 'sim-by-OH-sis',
    definition: 'A close, mutually beneficial relationship between two different organisms.',
    fact: 'Clownfish and sea anemones are a textbook example — the fish gets shelter, and the anemone gets cleaned and defended.',
    example: 'The documentary explored the surprising symbiosis between fungi and tree roots underground.',
  },
  {
    word: 'Nebula',
    pos: 'noun',
    pron: 'NEB-yuh-luh',
    definition: 'A vast cloud of gas and dust in space, often where new stars form.',
    fact: 'The word is simply Latin for "cloud" or "mist."',
    example: 'Through the telescope, the nebula glowed in soft shades of pink and blue.',
  },
  {
    word: 'Supernova',
    pos: 'noun',
    pron: 'SOO-per-noh-vuh',
    definition: 'The powerful explosion that marks the end of a massive star’s life.',
    fact: 'For a brief period, a single supernova can outshine its entire host galaxy.',
    example: 'Astronomers raced to observe the supernova before its brightness began to fade.',
  },
  {
    word: 'Equinox',
    pos: 'noun',
    pron: 'EE-kwuh-noks',
    definition: 'Either of the two days each year when day and night are nearly equal in length.',
    fact: 'It comes from Latin aequus (equal) and nox (night).',
    example: 'The autumn equinox marks the point where days start growing shorter than nights.',
  },
  {
    word: 'Solstice',
    pos: 'noun',
    pron: 'SOL-stis',
    definition: 'Either of the two days each year with the longest or shortest period of daylight.',
    fact: 'It comes from Latin sol (sun) and sistere (to stand still) — the point where the sun’s path seems to pause.',
    example: 'They stayed up all night to watch the sunrise on the summer solstice.',
  },
  {
    word: 'Tsunami',
    pos: 'noun',
    pron: 'tsoo-NAH-mee',
    definition: 'A large, powerful ocean wave usually caused by an underwater earthquake.',
    fact: 'It comes from Japanese tsu (harbor) and nami (wave) — literally "harbor wave."',
    example: 'The coastal town built new sirens to warn residents of an approaching tsunami.',
  },
  {
    word: 'Avalanche',
    pos: 'noun',
    pron: 'AV-uh-lanch',
    definition: 'A sudden, large mass of snow, ice, or rock sliding down a mountainside.',
    fact: 'The word descended into English from French Alpine dialects, tracking the very slopes it describes.',
    example: 'The ski patrol closed the trail after spotting conditions ripe for an avalanche.',
  },
  {
    word: 'Quasar',
    pos: 'noun',
    pron: 'KWAY-zar',
    definition: 'An extremely bright, distant celestial object powered by a supermassive black hole.',
    fact: 'The name is a blend of "quasi-stellar," since early astronomers thought they looked star-like through a telescope.',
    example: 'Light from the distant quasar had been traveling for billions of years before reaching Earth.',
  },
  {
    word: 'Pulsar',
    pos: 'noun',
    pron: 'PUL-sar',
    definition: 'A rapidly spinning neutron star that emits regular pulses of radiation.',
    fact: 'The name blends "pulsating star," describing the steady, lighthouse-like beam it sweeps through space.',
    example: 'The pulsar’s signal was so regular that early astronomers briefly wondered if it was artificial.',
  },
  {
    word: 'Osmosis',
    pos: 'noun',
    pron: 'oz-MOH-sis',
    definition: 'The movement of a liquid through a membrane toward the side with a higher concentration of dissolved substances.',
    fact: 'It comes from the Greek osmos, meaning "push" or "thrust."',
    example: 'The biology lesson demonstrated osmosis using a plastic bag, water, and sugar.',
  },
  {
    word: 'Mitosis',
    pos: 'noun',
    pron: 'my-TOH-sis',
    definition: 'The process by which a single cell divides into two identical daughter cells.',
    fact: 'It comes from the Greek mitos, "thread," referring to the thread-like appearance of chromosomes as they separate.',
    example: 'Under the microscope, the students could just make out a cell caught mid-mitosis.',
  },
  {
    word: 'Chlorophyll',
    pos: 'noun',
    pron: 'KLOR-uh-fil',
    definition: 'The green pigment in plants that captures sunlight for photosynthesis.',
    fact: 'French chemists coined the name in 1817 from Greek chloros (green) and phyllon (leaf).',
    example: 'Autumn leaves change color as their chlorophyll breaks down, revealing pigments that were there all along.',
  },
  {
    word: 'Stalactite',
    pos: 'noun',
    pron: 'stuh-LAK-tite',
    definition: 'A mineral formation that hangs down from a cave ceiling, formed by dripping water.',
    fact: 'A handy trick to remember it: stalaCtites cling tightly to the Ceiling, while stalaGmites Grow up from the Ground.',
    example: 'Water dripped slowly from the tip of a massive stalactite deep in the cave.',
  },
  {
    word: 'Doldrums',
    pos: 'noun',
    pron: 'DOHL-drumz',
    definition: 'A period of stagnation, inactivity, or low spirits.',
    fact: 'It originated as a sailors’ term for a real equatorial belt of calm ocean wind, where old sailing ships could sit becalmed for weeks.',
    example: 'Sales hit the summer doldrums, with almost nothing moving for weeks.',
  },

  /* ── Batch 2: quirky everyday words worth reviving ── */
  {
    word: 'Bumfuzzle',
    pos: 'verb',
    pron: 'BUM-fuz-ul',
    definition: 'To confuse or fluster someone.',
    fact: 'It’s genuine Southern American slang, still listed in major dictionaries despite sounding entirely made up.',
    example: 'The new remote control’s dozen buttons left grandpa completely bumfuzzled.',
  },
  {
    word: 'Higgledy-piggledy',
    pos: 'adverb',
    pron: 'HIG-ul-dee PIG-ul-dee',
    definition: 'In a jumbled, disorderly, haphazard way.',
    fact: 'This reduplicating phrase dates back to the 1500s, possibly echoing the jumbled scramble of pigs.',
    example: 'Books and papers were stacked higgledy-piggledy across his desk.',
  },
  {
    word: 'Argy-bargy',
    pos: 'noun',
    pron: 'AR-jee BAR-jee',
    definition: 'A lively, often good-natured argument or dispute.',
    fact: 'It’s a British and Scots reduplication of "argue," designed to sound as scrappy as it means.',
    example: 'A bit of friendly argy-bargy broke out over who’d get the last slice of pie.',
  },
  {
    word: 'Fuddy-duddy',
    pos: 'noun',
    pron: 'FUD-ee DUD-ee',
    definition: 'An old-fashioned, overly cautious, or fussy person.',
    fact: 'It’s been in casual English use since at least the early 1900s.',
    example: 'Don’t be such a fuddy-duddy — come dance with everyone else.',
  },
  {
    word: 'Nincompoop',
    pos: 'noun',
    pron: 'NIN-kum-poop',
    definition: 'A foolish or silly person.',
    fact: 'It first appeared in English in the late 1600s, though its exact origin remains genuinely debated.',
    example: 'Only a nincompoop would leave the car keys in the freezer.',
  },
  {
    word: 'Flimflam',
    pos: 'noun',
    pron: 'FLIM-flam',
    definition: 'Deceptive nonsense, especially a swindle or confidence trick.',
    fact: 'It’s another old reduplicative word, built simply to sound as slippery as its meaning.',
    example: 'The whole investment pitch turned out to be pure flimflam.',
  },
  {
    word: 'Brouhaha',
    pos: 'noun',
    pron: 'BROO-hah-hah',
    definition: 'A noisy commotion or excited reaction of surprise, anger, or fuss.',
    fact: 'English borrowed it from French in the 19th century, and it has kept its theatrical flair ever since.',
    example: 'A minor brouhaha erupted online after the trailer dropped a day early.',
  },
  {
    word: 'Persnickety',
    pos: 'adjective',
    pron: 'per-SNIK-uh-tee',
    definition: 'Excessively fussy or particular about small details.',
    fact: 'It’s an American alteration of the older Scots word "pernickety."',
    example: 'He’s famously persnickety about how his coffee is measured, down to the gram.',
  },
  {
    word: 'Discombobulate',
    pos: 'verb',
    pron: 'dis-kum-BOB-yuh-late',
    definition: 'To confuse or throw someone into a state of disorder.',
    fact: 'It began as a playful 19th-century American invention, likely riffing on "discompose."',
    example: 'The sudden schedule change left the whole office thoroughly discombobulated.',
  },
  {
    word: 'Flummox',
    pos: 'verb',
    pron: 'FLUM-uks',
    definition: 'To completely bewilder or confuse.',
    fact: 'It emerged from 19th-century English dialect, and its exact origin is still something of a mystery.',
    example: 'The final riddle flummoxed even the show’s most seasoned contestants.',
  },
  {
    word: 'Skulduggery',
    pos: 'noun',
    pron: 'skul-DUG-uh-ree',
    definition: 'Underhanded, dishonest, or trickery-filled behavior.',
    fact: 'It comes from the Scots word sculduddery, which originally described bawdy conduct rather than dishonesty.',
    example: 'The audit uncovered years of quiet financial skulduggery.',
  },

  /* ── Batch 2: vocabulary worth having in your back pocket ── */
  {
    word: 'Perspicacious',
    pos: 'adjective',
    pron: 'pur-spi-KAY-shus',
    definition: 'Having sharp insight or a keen understanding of things.',
    fact: 'It shares a Latin root with "spectacles" — both trace back to specere, "to look."',
    example: 'Her perspicacious editing caught a plot hole the whole writers’ room had missed.',
  },
  {
    word: 'Sesquipedalian',
    pos: 'adjective',
    pron: 'ses-kwi-puh-DAY-lee-un',
    definition: 'Given to using long, often unnecessarily complicated words.',
    fact: 'Wonderfully, the word literally means "measuring a foot and a half" in Latin — an ironically long word for long words.',
    example: 'His sesquipedalian writing style made the simplest memo feel like a legal document.',
  },
  {
    word: 'Pulchritude',
    pos: 'noun',
    pron: 'PUL-kri-tood',
    definition: 'Physical beauty.',
    fact: 'Despite describing something lovely, the word itself is often cited as one of English’s least pretty-sounding.',
    example: 'The judges scored contestants on poise as much as pulchritude.',
  },
  {
    word: 'Ebullient',
    pos: 'adjective',
    pron: 'ih-BUL-yent',
    definition: 'Overflowing with excitement, enthusiasm, or high spirits.',
    fact: 'It comes from Latin ebullire, "to boil over" — the feeling is right there in the roots.',
    example: 'She gave an ebullient speech after the team clinched the championship.',
  },
  {
    word: 'Effervescent',
    pos: 'adjective',
    pron: 'ef-er-VES-ent',
    definition: 'Vivacious and enthusiastic; also, bubbling with tiny gas bubbles.',
    fact: 'It shares its root with "boiling" — effervescere means "to boil up" in Latin.',
    example: 'Her effervescent energy made even the dullest meetings feel lively.',
  },
  {
    word: 'Halcyon',
    pos: 'adjective',
    pron: 'HAL-see-un',
    definition: 'Calm, peaceful, and happy — often used for a golden past period.',
    fact: 'It comes from Greek myth: the halcyon (a mythical kingfisher) was said to calm the winter sea to nest.',
    example: 'They often looked back fondly on the halcyon days of their college years.',
  },
  {
    word: 'Nefarious',
    pos: 'adjective',
    pron: 'nih-FAIR-ee-us',
    definition: 'Wicked or criminal in a deliberate, calculated way.',
    fact: 'It comes from Latin nefas, "wrong" or "impious" — something forbidden even by divine law.',
    example: 'The cartoon villain revealed his nefarious plan just before the final act.',
  },
  {
    word: 'Obfuscate',
    pos: 'verb',
    pron: 'OB-fuh-skate',
    definition: 'To deliberately make something unclear or difficult to understand.',
    fact: 'It comes from Latin roots meaning "to darken" — obfuscation is, quite literally, casting shade on meaning.',
    example: 'The report seemed designed to obfuscate rather than explain the budget shortfall.',
  },
  {
    word: 'Ubiquitous',
    pos: 'adjective',
    pron: 'yoo-BIK-wi-tus',
    definition: 'Present, appearing, or found everywhere.',
    fact: 'It comes from Latin ubique, simply meaning "everywhere."',
    example: 'Smartphones have become so ubiquitous that a landline now feels like an antique.',
  },
  {
    word: 'Sycophant',
    pos: 'noun',
    pron: 'SIK-uh-fant',
    definition: 'A person who flatters someone powerful for personal gain.',
    fact: 'Its ancient Greek root, sykophantēs, literally combines "fig" and "to show" — though scholars still debate exactly what showing a fig had to do with flattery.',
    example: 'The court was full of sycophants eager to agree with whatever the king said.',
  },
  {
    word: 'Vicissitude',
    pos: 'noun',
    pron: 'vih-SIS-ih-tood',
    definition: 'A change of circumstance or fortune, especially an unwelcome one.',
    fact: 'It comes from Latin vicis, meaning "change" or "turn" — the same root behind "vice versa."',
    example: 'Through every vicissitude of her career, she kept the same small notebook of ideas.',
  },
  {
    word: 'Surreptitious',
    pos: 'adjective',
    pron: 'sur-ep-TISH-us',
    definition: 'Done secretly, so as to avoid being noticed.',
    fact: 'It comes from Latin surripere, "to snatch secretly," a root it shares with the idea of stealing something unseen.',
    example: 'He took a surreptitious glance at his phone under the table during the meeting.',
  },
  {
    word: 'Idiosyncrasy',
    pos: 'noun',
    pron: 'id-ee-oh-SIN-kruh-see',
    definition: 'A distinctive, sometimes odd habit or trait particular to a person.',
    fact: 'It comes from Greek idios (one’s own) plus syn and krasis (a mixture) — literally, one’s own personal blend.',
    example: 'One charming idiosyncrasy of his was narrating his own cooking out loud.',
  },
  {
    word: 'Euphoria',
    pos: 'noun',
    pron: 'yoo-FOR-ee-uh',
    definition: 'A powerful feeling of happiness, excitement, and well-being.',
    fact: 'It comes from Greek eu (well) and pherein (to bear) — literally, "bearing up well."',
    example: 'A rush of euphoria swept over the runners as they crossed the finish line.',
  },
  {
    word: 'Wistful',
    pos: 'adjective',
    pron: 'WIST-ful',
    definition: 'Filled with a vague, gentle longing, often for something in the past.',
    fact: 'It likely developed from the older word "wishful," gradually taking on a softer, more melancholy shade of meaning.',
    example: 'She smiled a wistful smile, thinking of summers at her grandmother’s house.',
  },
  {
    word: 'Verisimilitude',
    pos: 'noun',
    pron: 'ver-uh-si-MIL-ih-tood',
    definition: 'The appearance of being true or real.',
    fact: 'It comes from Latin verus (true) and similis (similar) — something that resembles truth closely enough to convince.',
    example: 'The film’s costume department worked for months to get the era’s verisimilitude just right.',
  },
  {
    word: 'Insouciant',
    pos: 'adjective',
    pron: 'in-SOO-see-unt',
    definition: 'Carefree and casually unconcerned, in a relaxed rather than careless way.',
    fact: 'It comes from French, built from in- (not) and souci (worry, care).',
    example: 'He gave an insouciant shrug, as though the deadline didn’t exist.',
  },
  {
    word: 'Panacea',
    pos: 'noun',
    pron: 'pan-uh-SEE-uh',
    definition: 'A solution or remedy claimed to fix every problem.',
    fact: 'It’s named for Panacea, the Greek goddess of universal healing.',
    example: 'No single app is a panacea for a company’s deeper communication problems.',
  },
  {
    word: 'Epiphany',
    pos: 'noun',
    pron: 'ih-PIF-uh-nee',
    definition: 'A sudden, striking moment of realization or insight.',
    fact: 'It comes from the Christian feast of the Epiphany, originally marking a divine "manifestation," which later broadened into any sudden flash of understanding.',
    example: 'Halfway through the hike, she had a quiet epiphany about what she really wanted to do next.',
  },
  {
    word: 'Juxtaposition',
    pos: 'noun',
    pron: 'juks-tuh-puh-ZISH-un',
    definition: 'The act of placing two contrasting things close together for effect.',
    fact: 'It comes from Latin juxta (next to) plus "position."',
    example: 'The photo’s power came from the juxtaposition of a tiny child beside a towering skyscraper.',
  },
  {
    word: 'Conundrum',
    pos: 'noun',
    pron: 'kuh-NUN-drum',
    definition: 'A confusing or difficult problem, especially one with no easy solution.',
    fact: 'The word first turns up in 16th-century Oxford student slang, possibly invented as a mock-Latin joke.',
    example: 'Fitting three meetings into one hour was a scheduling conundrum with no clean answer.',
  },
  {
    word: 'Bibliophile',
    pos: 'noun',
    pron: 'BIB-lee-uh-file',
    definition: 'A person who loves and collects books.',
    fact: 'It combines Greek biblion (book) and philos (loving) — the same roots behind "Bible" and "philosophy."',
    example: 'As a lifelong bibliophile, she could never walk past a used bookstore without going in.',
  },
  {
    word: 'Logophile',
    pos: 'noun',
    pron: 'LOG-uh-file',
    definition: 'A person who loves words.',
    fact: 'It combines Greek logos (word) and philos (loving) — basically, the perfect word for anyone who reads a Word of the Day.',
    example: 'Every logophile on the team had a favorite obscure word they loved to slip into conversation.',
  },
];

/*
 * Pick the word for a given date, deterministically. The index is derived from
 * the day number (days since the Unix epoch), so it is stable for a whole day
 * everywhere, and simply cycles through the list as the days advance.
 */
export function getWordOfTheDay(date = new Date()) {
  const dayNumber = Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000
  );
  const len = WORDS.length;
  const idx = ((dayNumber % len) + len) % len;
  return WORDS[idx];
}
